import type {
  AutomaticFirstHop,
  GraphPathLookupHostToDefaultGWParameters,
  GraphPathLookupMulticastParameters,
  GraphPathLookupUnicastParameters,
} from '../../api';
import {
  createPathlookupGraphLink,
  createTopologyGraphLink,
} from '../../graph/index';
import { cloudConstructNodeTypeFromVendor } from '../../nodes/types';
import {
  CLOUD_CONSTRUCT_LINK_STATE_KEY,
  type CloudConstructLinkPayload,
  type CloudConstructLinkState,
  type OasTableColumnLink,
  type OasTableColumnLinkCloudConstruct,
  type OasTableColumnLinkGraphPathLookup,
} from '../../oas/OasTableColumnLink';
import type { PathLookupIpProtocol } from '../../pathLookup';
import { safeAbsurd } from '../../types/index';
import { isObject } from '../../types/isObject';
import type { Filter, FilterValue, Filters } from '../filters/filters';
import { fixEqEmptyFilter } from '../filters/fixEqEmptyFilter';
import {
  type TableLink,
  createTableFilterLink,
  createTableLink,
} from '../links/TableLink';

const readString = (row: Record<string, unknown>, field: string) => {
  const value = row[field];
  return typeof value === 'string' && value !== '' ? value : null;
};

/**
 * Collect the Explorer's `extra` values, skipping columns the row does not
 * carry — the Explorer treats a missing key the same as an absent filter.
 */
const collectExtra = (
  record: Record<string, unknown>,
  extraFields: Record<string, string>,
): Record<string, string> => {
  const extra: Record<string, string> = {};
  for (const [extraKey, column] of Object.entries(extraFields)) {
    const value = readString(record, column);
    if (value !== null) {
      extra[extraKey] = value;
    }
  }

  return extra;
};

/**
 * Resolve the row into the cloud construct the Device Explorer should open, or
 * null when the row does not describe one — an unsatisfied `linkWhen` gate, a
 * missing or unsupported vendor, or no id and nothing to compose one from.
 */
const resolveCloudConstruct = (
  record: Record<string, unknown>,
  link: OasTableColumnLinkCloudConstruct,
  idField: string,
  extraFields: Record<string, string>,
): CloudConstructLinkPayload | null => {
  // Gate on a sibling value when the table asks for it, e.g. Public IPs links
  // `resourceId` only when the resource really is a network interface.
  if (
    link.linkWhen &&
    readString(record, link.linkWhen.field) !== link.linkWhen.equals
  ) {
    return null;
  }

  // Vendor-specific tables state the vendor; shared tables carry it per row.
  const vendor = link.vendor ?? readString(record, 'cloudProvider');
  if (
    vendor === null ||
    cloudConstructNodeTypeFromVendor(vendor, link.constructKind) === null
  ) {
    return null;
  }

  const extra = collectExtra(record, extraFields);

  // Most constructs are identified by their own id. Some (e.g. GCP peerings)
  // have none, and are identified by the composite of their `extra` values —
  // the same way the diagram resolves them.
  const constructId = readString(record, idField);
  if (constructId === null && Object.keys(extra).length === 0) {
    return null;
  }

  return {
    constructKind: link.constructKind,
    constructId: constructId ?? '',
    vendor,
    name: link.nameField ? readString(record, link.nameField) : null,
    extra,
  };
};

const createOrFilter = (column: string, originalFilter: any): any => {
  const filter = [...originalFilter];
  const filterValues = new Set(filter.pop());

  return {
    or: Array.from(filterValues).map((f) => ({ [column]: [...filter, f] })),
  };
};

/**
 * If filled value is array or boolean, we need to reformat filter to advanced format since simple filters
 * can't handle such values
 */
const maybeExpandToAdvancedFilter = (filter: Record<string, any>): any => {
  const isUnsupportedBySimple = (f: unknown) =>
    Array.isArray(f) || typeof f === 'boolean';
  if (
    Object.values(filter).some((f) => isUnsupportedBySimple(f[f.length - 1]))
  ) {
    return {
      and: Object.entries(filter).map(([column, filterArr]) =>
        Array.isArray(filterArr[filterArr.length - 1])
          ? createOrFilter(column, filterArr)
          : { [column]: filterArr },
      ),
    };
  }

  return filter;
};
/**
 * Replace all strings starting with $ in a deep object structure with a value from row
 *
 * For example, $sn will be replaced by value of row.sn
 */
const makeFillTableFilterTemplate =
  (filterTemplate: Filters) =>
  // we can only dream about type safety in this blind deep clone of a template object
  // filled with unknown row of data
  (row: any, currentValue?: FilterValue) => {
    const fillTemplateElement = (element: any): any => {
      if (Array.isArray(element)) {
        return fixEqEmptyFilter(element.map(fillTemplateElement) as Filter);
      }
      if (isObject(element)) {
        return Object.entries(element).reduce((acc, [key, value]) => {
          acc[key] = fillTemplateElement(value);
          return acc;
        }, {} as any);
      }
      if (element === '$currentValue') {
        return currentValue ?? null;
      }
      if (typeof element === 'string' && element.startsWith('$')) {
        return row[element.substring(1)];
      }
      return element;
    };
    const filter = fillTemplateElement(filterTemplate);

    return maybeExpandToAdvancedFilter(filter);
  };

/**
 * Collect all strings starting with $ in a deep object structure
 *
 * Return them as an array without the starting $
 */
const collectTableFilterDependencies = (filterTemplate: Filters): string[] => {
  const dependencies = new Set<string>();
  const collectElementDependencies = (element: any): any => {
    if (Array.isArray(element)) {
      element.forEach(collectElementDependencies);
    } else if (isObject(element)) {
      Object.values(element).forEach(collectElementDependencies);
    } else if (
      typeof element === 'string' &&
      element.startsWith('$') &&
      element !== '$currentValue'
    ) {
      dependencies.add(element.substring(1));
    }
  };
  collectElementDependencies(filterTemplate);
  return [...dependencies];
};

const getPathLookoutGraphParameters = (
  link: OasTableColumnLinkGraphPathLookup,
  row: Pick<any, string>,
) => {
  const advancedOptions = {
    enableRegions: false,
    srcRegions: '.*',
    dstRegions: '.*',
    ttl: 128,
    fragmentOffset: 0,
    otherOptions: {
      applications: '',
      applicationsMatchWithRegex: true,
      category: '',
      tracked: false,
      url: '',
    },
    protocol: 'tcp' as PathLookupIpProtocol,
    l4Options: {
      flags: [],
      dstPorts: '80',
      srcPorts: '10000',
    },
    firstHopAlgorithm: { type: 'automatic' } as AutomaticFirstHop,
    securedPath: true,
  };

  switch (link.pathLookupType) {
    case 'unicast':
      return {
        type: 'pathLookup',
        pathLookupType: 'unicast',
        groupBy: link.groupBy,

        startingPoint: row[link.startingPointColumn],
        destinationPoint: row[link.destinationPointColumn],
        networkMode: false,
        ...advancedOptions,
      } satisfies GraphPathLookupUnicastParameters;
    case 'multicast':
      return {
        type: 'pathLookup',
        pathLookupType: 'multicast',
        groupBy: link.groupBy,

        group: row[link.groupColumn],
        source: row[link.sourceColumn],
        ...(link.receiverColumn && { receiver: row[link.receiverColumn] }),
        ...advancedOptions,
      } satisfies GraphPathLookupMulticastParameters;
    case 'hostToDefaultGW':
      return {
        type: 'pathLookup',
        pathLookupType: 'hostToDefaultGW',
        groupBy: link.groupBy,

        startingPoint: row[link.startingPointColumn],
      } satisfies GraphPathLookupHostToDefaultGWParameters;
    default:
      safeAbsurd(link, 'unknown pathLookupType');
      return undefined;
  }
};

export const createTableColumnLinkFromOas = (
  link: OasTableColumnLink,
  /** Host column's own field, needed by links whose availability depends on the cell's own value (e.g. `cloudSecurityTree`'s rule count, `cloudConstruct`'s default id). */
  columnKey: string,
  /** Set from `ENABLE_NEW_CLOUD_MODEL`; `false` keeps `cloudConstruct` cells as plain text. */
  isNewCloudModel?: boolean,
): TableLink<any> | undefined => {
  switch (link.type) {
    case 'cloudConstruct': {
      // The Explorer this link opens is built on the new cloud model, so with
      // the feature flag off the cell has to stay plain text
      if (isNewCloudModel === false) {
        return undefined;
      }

      const idField = link.idField ?? columnKey;
      const extraFields = link.extraFields ?? {};

      return createTableLink(
        (row: Pick<any, string>) => {
          // The Explorer is in-page state with no route of its own, so — like
          // the `cloudSecurityTree` link below — stay on the current route and
          // hand the construct over in `state`. Rows that resolve to no
          // construct get no link, leaving the cell as plain text.
          const payload = resolveCloudConstruct(
            row as Record<string, unknown>,
            link,
            idField,
            extraFields,
          );

          return payload === null
            ? undefined
            : {
                to: '.',
                state: {
                  [CLOUD_CONSTRUCT_LINK_STATE_KEY]: payload,
                } satisfies CloudConstructLinkState,
              };
        },
        [
          columnKey,
          idField,
          ...(link.vendor ? [] : ['cloudProvider']),
          ...(link.nameField ? [link.nameField] : []),
          ...(link.linkWhen ? [link.linkWhen.field] : []),
          ...Object.values(extraFields),
        ],
      );
    }
    case 'table':
      return createTableFilterLink(
        link.target,
        makeFillTableFilterTemplate(link.filter),
        collectTableFilterDependencies(link.filter),
        link.icon === false,
      );
    case 'topology':
      return createTableLink(
        (row) =>
          createTopologyGraphLink({
            parameters: {
              paths: [row[link.attributeField]],
              groupBy: link.groupBy,
              ...(link.stpInstancesChart
                ? {
                    technologies: {
                      stpInstances: {
                        isolate: true,
                        instances: [
                          {
                            rootId: row[link.stpInstancesChart.rootIdColumn],
                            vlanId: row[link.stpInstancesChart.vlanIdColumn],
                            visible: true,
                            grouped: false,
                          },
                        ],
                      },
                    },
                  }
                : {}),
            },
          }),
        [link.attributeField],
      );
    case 'pathLookup':
      return createTableLink(
        (row: Pick<any, string>) =>
          createPathlookupGraphLink({
            parameters: getPathLookoutGraphParameters(link, row),
          }),
        [],
      );
    case 'cloudSecurityTree':
      return createTableLink(
        (row: Pick<any, string>) => {
          const resourceName = row[link.resourceNameField];
          const ruleCount = columnKey ? row[columnKey] : undefined;
          return resourceName && ruleCount
            ? {
                to: '.',
                state: {
                  cloudSecurityTreeModal: {
                    resourceName,
                    direction: link.direction,
                  },
                },
              }
            : undefined;
        },
        [link.resourceNameField],
      );
    default:
      safeAbsurd(link);
      return undefined;
  }
};
